import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/api/client";

interface AISuggestion {
  id: string;
  targetType: string;
  targetId: string;
  type: string;
  suggestedValue: string;
  originalValue: string | null;
  status: "pending" | "accepted" | "rejected" | "modified" | "expired";
  provider: string;
  model: string;
  confidence: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  processingTimeMs: number | null;
  requestedBy: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  appliedValue: string | null;
  createdAt: string;
}

interface AIStatus {
  enabled: boolean;
  providers: Record<
    string,
    {
      enabled: boolean;
      models: Record<string, { name: string; contextWindow: number; description: string }>;
      defaultModel: string;
    }
  >;
  pendingSuggestions: number;
}

const SUGGESTION_TYPE_LABELS: Record<string, string> = {
  summary: "Résumé",
  keywords: "Mots-clés",
  classification: "Classification",
  subject_headings: "Vedettes-matières",
  abstract: "Résumé détaillé",
  translation: "Traduction",
  enrichment: "Enrichissement",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: {
    label: "En attente",
    color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
  },
  accepted: {
    label: "Acceptée",
    color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
  },
  rejected: {
    label: "Rejetée",
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
  },
  modified: {
    label: "Modifiée",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
  },
  expired: {
    label: "Expirée",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200",
  },
};

function SuggestionCard({
  suggestion,
  onReview,
}: {
  suggestion: AISuggestion;
  onReview: (id: string, action: "accept" | "reject", notes?: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState("");
  const isPending = suggestion.status === "pending";

  const formatValue = (value: string) => {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return (parsed as string[]).join(", ");
      }
      if (typeof parsed === "object" && parsed !== null) {
        return JSON.stringify(parsed, null, 2);
      }
      return value;
    } catch {
      return value;
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between p-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {SUGGESTION_TYPE_LABELS[suggestion.type] ?? suggestion.type}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_LABELS[suggestion.status]?.color ?? ""}`}
            >
              {STATUS_LABELS[suggestion.status]?.label ?? suggestion.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {suggestion.provider} / {suggestion.model}
            {suggestion.confidence !== null &&
              ` • Confiance: ${(suggestion.confidence * 100).toFixed(0)}%`}
          </p>
        </div>
        <button
          onClick={() => {
            setExpanded(!expanded);
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          {/* Suggested value */}
          <div className="mb-4">
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Valeur suggérée
            </p>
            <div className="rounded-lg bg-green-50 p-3 text-sm text-green-900 dark:bg-green-900/20 dark:text-green-100">
              <pre className="font-sans whitespace-pre-wrap">
                {formatValue(suggestion.suggestedValue)}
              </pre>
            </div>
          </div>

          {/* Original value */}
          {suggestion.originalValue && (
            <div className="mb-4">
              <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                Valeur actuelle
              </p>
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-900 dark:bg-gray-900/50 dark:text-gray-100">
                <pre className="font-sans whitespace-pre-wrap">
                  {formatValue(suggestion.originalValue)}
                </pre>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="mb-4 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
            {suggestion.inputTokens !== null && (
              <span>Entrée: {suggestion.inputTokens} tokens</span>
            )}
            {suggestion.outputTokens !== null && (
              <span>Sortie: {suggestion.outputTokens} tokens</span>
            )}
            {suggestion.processingTimeMs !== null && (
              <span>Temps: {suggestion.processingTimeMs}ms</span>
            )}
            <span>Créée: {new Date(suggestion.createdAt).toLocaleString("fr-FR")}</span>
          </div>

          {/* Review actions */}
          {isPending && (
            <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                placeholder="Notes de révision (optionnel)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onReview(suggestion.id, "accept", notes);
                  }}
                  className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
                >
                  ✓ Accepter
                </button>
                <button
                  onClick={() => {
                    onReview(suggestion.id, "reject", notes);
                  }}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
                >
                  ✗ Rejeter
                </button>
              </div>
            </div>
          )}

          {/* Review info for reviewed suggestions */}
          {!isPending && suggestion.reviewedBy && (
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <p>
                Révisée par {suggestion.reviewedBy} le{" "}
                {suggestion.reviewedAt
                  ? new Date(suggestion.reviewedAt).toLocaleString("fr-FR")
                  : ""}
              </p>
              {suggestion.reviewNotes && <p className="mt-1">Notes: {suggestion.reviewNotes}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AISuggestionsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>("pending");

  // Fetch AI status
  const { data: status } = useQuery<AIStatus>({
    queryKey: ["ai-status"],
    queryFn: () => get<AIStatus>("/api/ai/status"),
  });

  // Fetch suggestions
  const { data, isLoading, error } = useQuery<{ suggestions: AISuggestion[] }>({
    queryKey: ["ai-suggestions", filter],
    queryFn: () => {
      const params = filter !== "all" ? `?type=${filter}` : "";
      return get<{ suggestions: AISuggestion[] }>(`/api/ai/suggestions${params}`);
    },
  });

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: ({
      id,
      action,
      notes,
    }: {
      id: string;
      action: string;
      notes: string | undefined;
    }) =>
      post<{ suggestion: AISuggestion; applied: boolean }>(`/api/ai/suggestions/${id}/review`, {
        action,
        notes,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ai-suggestions"] });
      void queryClient.invalidateQueries({ queryKey: ["ai-status"] });
    },
  });

  const handleReview = useCallback(
    (id: string, action: "accept" | "reject", notes?: string) => {
      reviewMutation.mutate({ id, action, notes });
    },
    [reviewMutation]
  );

  if (!status?.enabled) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Enrichissement IA</h1>
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-6 text-center dark:border-yellow-800 dark:bg-yellow-900/20">
          <span className="text-4xl">🤖</span>
          <h2 className="mt-4 text-lg font-medium text-yellow-900 dark:text-yellow-100">
            L'enrichissement IA est désactivé
          </h2>
          <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
            Configurez une clé API (Claude ou OpenAI) pour activer cette fonctionnalité.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Suggestions IA</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {status.pendingSuggestions} suggestion(s) en attente de révision
          </p>
        </div>
      </div>

      {/* Provider status */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          Fournisseurs IA
        </h2>
        <div className="flex flex-wrap gap-3">
          {Object.entries(status.providers).map(([name, provider]) => (
            <div
              key={name}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                provider.enabled
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              }`}
            >
              <span>{provider.enabled ? "✓" : "○"}</span>
              <span className="font-medium capitalize">{name}</span>
              {provider.enabled && (
                <span className="text-xs opacity-75">({provider.defaultModel})</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {[
          { value: "pending", label: "En attente" },
          { value: "summary", label: "Résumés" },
          { value: "keywords", label: "Mots-clés" },
          { value: "classification", label: "Classifications" },
          { value: "all", label: "Tout" },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => {
              setFilter(option.value);
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === option.value
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Suggestions list */}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          Erreur lors du chargement des suggestions
        </div>
      ) : data?.suggestions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <span className="text-4xl">📭</span>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Aucune suggestion {filter === "pending" ? "en attente" : ""}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.suggestions.map((suggestion) => (
            <SuggestionCard key={suggestion.id} suggestion={suggestion} onReview={handleReview} />
          ))}
        </div>
      )}
    </div>
  );
}
